import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { formatDateTime } from "../../../utils/dayjs";
import Swal from "sweetalert2";
// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: {
    display: "flex",
    gap: "0",
    minHeight: "100vh",
    background: "#f3f2ef",
    boxSizing: "border-box",
  },

  // ── Sidebar ──
  sidebar: {
    width: "260px",
    flexShrink: 0,
    borderRight: "1px solid #e0ddd6",
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    position: "sticky",
    top: 0,
    overflowY: "auto",
  },
  sidebarTop: {
    padding: "20px 20px 12px",
    borderBottom: "1px solid #e0ddd6",
  },
  sidebarTicketId: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#999",
    marginBottom: "6px",
  },
  sidebarSubject: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a18",
    lineHeight: 1.4,
    marginBottom: "10px",
  },
  sidebarSection: {
    padding: "14px 20px",
    borderBottom: "1px solid #e0ddd6",
  },
  sidebarSectionLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#aaa",
    marginBottom: "10px",
  },
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    fontSize: "13px",
  },
  metaKey: { color: "#888", flexShrink: 0 },
  metaVal: { fontWeight: 500, color: "#1a1a18", textAlign: "right" },

  badge: (status) => {
    const map = {
      Open:    { bg: "#e6f4ea", color: "#2d7a3a" },
      Closed:  { bg: "#fce8e8", color: "#b52b2b" },
      Pending: { bg: "#fef3e0", color: "#a06000" },
    };
    const t = map[status] || map["Pending"];
    return {
      display: "inline-block",
      fontSize: "11px",
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: "20px",
      background: t.bg,
      color: t.color,
    };
  },

  closeBtn: {
    display: "block",
    width: "calc(100% - 40px)",
    margin: "14px 20px 0",
    padding: "9px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid #f0c0c0",
    background: "transparent",
    color: "#b52b2b",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 0.15s",
    textAlign: "center",
  },
  actionBtn: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "7px 0",
    fontSize: "13px",
    background: "transparent",
    border: "none",
    color: "#444",
    cursor: "pointer",
    fontFamily: "inherit",
    borderBottom: "1px solid #f0efe9",
  },

  // ── Main ──
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    maxWidth: "780px",
    margin: "0 auto",
    width: "100%",
    padding: "0 0 60px",
  },

  // ── Thread header ──
  threadHeader: {
    padding: "24px 32px 16px",
    background: "#f3f2ef",
    position: "sticky",
    top: 0,
    zIndex: 10,
    borderBottom: "1px solid #e0ddd6",
  },
  threadSubject: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#1a1a18",
    marginBottom: "6px",
  },
  threadMeta: {
    fontSize: "13px",
    color: "#888",
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
  },

  // ── Email thread ──
  thread: {
    padding: "24px 32px",
    display: "flex",
    flexDirection: "column",
    gap: "0",
  },

  // ── Email card ──
  emailCard: (expanded) => ({
    background: "#ffffff",
    border: "1px solid #e0ddd6",
    borderRadius: "10px",
    marginBottom: "10px",
    overflow: "hidden",
    cursor: expanded ? "default" : "pointer",
    transition: "box-shadow 0.15s",
  }),
  emailHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
  },
  avatarCircle: (isUser) => ({
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: isUser ? "#ddeeff" : "#e8f5e9",
    color: isUser ? "#1565c0" : "#2d7a3a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 700,
    flexShrink: 0,
  }),
  emailHeaderInfo: { flex: 1, minWidth: 0 },
  emailSender: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a18",
    marginBottom: "2px",
  },
  emailPreview: {
    fontSize: "13px",
    color: "#888",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "400px",
  },
  emailTime: {
    fontSize: "12px",
    color: "#aaa",
    whiteSpace: "nowrap",
    marginLeft: "auto",
  },
  emailDivider: {
    height: "1px",
    background: "#f0efe9",
    margin: "0 18px",
  },
  emailBody: {
    padding: "18px 18px 14px",
    fontSize: "14px",
    lineHeight: 1.7,
    color: "#2c2c2a",
  },
  emailFooter: {
    padding: "8px 18px 14px",
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  attachment: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: "#1565c0",
    background: "#f0f4ff",
    padding: "4px 10px",
    borderRadius: "6px",
    textDecoration: "none",
    border: "1px solid #d0dcf8",
  },

  // ── Reply box ──
  replyBox: {
    margin: "8px 32px 0",
    background: "#ffffff",
    border: "1px solid #e0ddd6",
    borderRadius: "10px",
    overflow: "hidden",
  },
  replyHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 18px",
    borderBottom: "1px solid #f0efe9",
    fontSize: "13px",
    color: "#888",
  },
  replyToTag: {
    background: "#f3f2ef",
    border: "1px solid #ddd",
    borderRadius: "5px",
    padding: "2px 8px",
    fontSize: "12px",
    color: "#444",
    fontWeight: 500,
  },
  replyTextarea: {
    width: "100%",
    padding: "16px 18px",
    fontSize: "14px",
    fontFamily: "inherit",
    border: "none",
    outline: "none",
    resize: "none",
    color: "#1a1a18",
    background: "transparent",
    lineHeight: 1.65,
    boxSizing: "border-box",
  },
  replyToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 18px",
    borderTop: "1px solid #f0efe9",
    background: "#fafaf8",
  },
  attachLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: "#666",
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #e0ddd6",
    background: "transparent",
    transition: "background 0.12s",
    fontFamily: "inherit",
  },
  fileChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "12px",
    color: "#444",
    background: "#f0efe9",
    padding: "3px 9px",
    borderRadius: "5px",
    marginLeft: "8px",
  },
  sendBtn: (disabled) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 20px",
    fontSize: "13px",
    fontWeight: 600,
    background: disabled ? "#ccc" : "#1a73e8",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit",
    transition: "background 0.15s",
    letterSpacing: "0.01em",
  }),

  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    fontFamily: "'DM Sans', sans-serif",
    color: "#888",
    fontSize: "14px",
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "AA";



const stripHtml = (html = "") =>
  html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// ─── EmailCard ────────────────────────────────────────────────────────────────
const EmailCard = ({ msg, imageBase, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isUser = msg.sender_type === "user";
  const senderName =  (isUser ? msg.sender_name : "Vekart (Support team)" );

  return (
    <div
      style={S.emailCard(expanded)}
      onClick={() => !expanded && setExpanded(true)}
      onMouseEnter={(e) => {
        if (!expanded) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={S.emailHeader}>
        <div style={S.avatarCircle(isUser)}>{getInitials(senderName)}</div>
        <div style={S.emailHeaderInfo}>
          <div style={S.emailSender}>{senderName}</div>
          {!expanded && (
            <div style={S.emailPreview}>{stripHtml(msg.message)}</div>
          )}
          {expanded && (
            <div style={{ fontSize: "12px", color: "#aaa" }}>
              {isUser ? "to Vekart (Support team)" : "to Customer"}
            </div>
          )}
        </div>
        <div style={S.emailTime}>{formatDateTime(msg.created_at)}</div>
        {expanded && (
          <button
            style={{
              marginLeft: "8px", background: "transparent", border: "none",
              cursor: "pointer", color: "#aaa", fontSize: "16px", padding: "0 4px",
            }}
            onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
            title="Collapse"
          >
            ∧
          </button>
        )}
      </div>

      {expanded && (
        <>
          <div style={S.emailDivider} />
          <div
            style={S.emailBody}
            dangerouslySetInnerHTML={{ __html: msg.message }}
          />
          {msg.attachments && msg.attachments.length > 0 && (
  <>
    <div style={S.emailDivider} />
    <div style={S.emailFooter}>
      {msg.attachments.map((file, index) => (
        <a
          key={index}
          href={`${imageBase}${file.file_path}`}
          target="_blank"
          rel="noreferrer"
          style={S.attachment}
          onClick={(e) => e.stopPropagation()}
        >
          📎 {file.file_name}
        </a>
      ))}
    </div>
  </>
)}
        </>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const TicketView = () => {
  const { id } = useParams();
  const baseurl = process.env.REACT_APP_BASE_URL;
  const imageBase = process.env.REACT_APP_IMAGE_URL;

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchTicket = async () => {
    try {
      const res = await axios.get(`${baseurl}/admin/tickets/${id}`);
      setTicket(res.data.ticket);
      setMessages(res.data.messages);

       await fetch(
        `${baseurl}/admin/tickets/${id}/viewed`,
        {
          method: "PUT",
        }
      );
    } catch (err) {
      console.error("Fetch failed", err);
    }
  };

  useEffect(() => { fetchTicket(); }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleReply = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("message", message);
      fd.append("sender_type", "user");
      for (let i = 0; i < files.length; i++) fd.append("files", files[i]);
      await axios.post(`${baseurl}/admin/tickets/${id}/reply`, fd);
      setMessage("");
      setFiles([]);
      await fetchTicket();
    } catch (err) {
      console.error("Reply failed", err);
    } finally {
      setSending(false);
    }
  };

 const handleCloseTicket = async () => {
  const result = await Swal.fire({
    title: "Close Ticket?",
    text: "This will close the ticket.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    confirmButtonText: "Yes, close it",
  });

  if (result.isConfirmed) {
    await axios.post(`${baseurl}/admin/tickets/${id}/close`);
    fetchTicket();
  }
};
  const handleResolve = async () => {
  const result = await Swal.fire({
    title: "Are you sure?",
    text: "You want to mark this ticket as resolved.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, mark as resolved",
  });

  if (result.isConfirmed) {
    try {
      await axios.post(`${baseurl}/admin/tickets/${id}/resolve`);

      Swal.fire({
        icon: "success",
        title: "Resolved!",
        text: "Ticket marked as resolved successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchTicket();
    } catch (err) {
      Swal.fire("Error", "Something went wrong!", "error");
    }
  }
};

  if (!ticket) return <div style={S.loading}>Loading ticket…</div>;

  return (
    <div className="page-content">
    <div style={S.page}>
      {/* ── Sidebar ── */}
      <div style={S.sidebar}>
        <div style={S.sidebarTop}>
          <div style={S.sidebarTicketId}>Ticket #{ticket.id}</div>
          <div style={S.sidebarSubject}>{ticket.subject}</div>
          <span style={S.badge(ticket.status)}>{ticket.status}</span>
        </div>

        <div style={S.sidebarSection}>
          <div style={S.sidebarSectionLabel}>Details</div>
          {[
            ["Department", ticket.department],
            ["Priority",   ticket.priority],
            ["Opened",     ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : "—"],
            ["Replies",    messages.length],
          ].map(([k, v]) => (
            <div style={S.metaRow} key={k}>
              <span style={S.metaKey}>{k}</span>
              <span style={S.metaVal}>{v}</span>
            </div>
          ))}
        </div>

        <div style={S.sidebarSection}>
  <div style={S.sidebarSectionLabel}>Actions</div>

  
  <button style={S.actionBtn} >
    Change department
  </button>

  <button style={S.actionBtn} onClick={handleResolve}>
    Mark as resolved
  </button>
</div>

        {ticket.status !== "Closed" && (
          <button
            style={S.closeBtn}
            onClick={handleCloseTicket}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fff0f0")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            Close ticket
          </button>
        )}
      </div>

      {/* ── Main ── */}
      <div style={S.main}>
        {/* Thread header */}
        <div style={S.threadHeader}>
          <div style={S.threadSubject}>{ticket.subject}</div>
          <div style={S.threadMeta}>
            <span>#{ticket.id}</span>
            <span>·</span>
            <span>{ticket.department}</span>
            <span>·</span>
            <span>{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

        {/* Closed banner */}
        {ticket.status === "Closed" && (
          <div style={{
            margin: "16px 32px 0",
            padding: "10px 16px",
            background: "#fff8e1",
            border: "1px solid #ffe082",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#7a5c00",
          }}>
            ⚠ This ticket is closed. Sending a reply will reopen it.
          </div>
        )}

        {/* Email thread */}
        <div style={S.thread}>
          {messages.map((msg, i) => (
            <EmailCard
              key={i}
              msg={msg}
              imageBase={imageBase}
              defaultExpanded={i === messages.length - 1}
            />
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Reply box */}
        <div style={S.replyBox}>
          <div style={S.replyHeader}>
            <span style={{ color: "#888", fontSize: "13px" }}>Reply to</span>
            <span style={S.replyToTag}>{ticket.customer_email || "customer"}</span>
          </div>

          <textarea
            style={S.replyTextarea}
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.ctrlKey || e.metaKey) && handleReply()}
            placeholder="Write your reply here…"
          />

          <div style={S.replyToolbar}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <label
                style={S.attachLabel}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f2ef")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M14 8.5L7.5 15a4.243 4.243 0 0 1-6-6L9 1a2.828 2.828 0 0 1 4 4L6 12a1.414 1.414 0 0 1-2-2L10.5 4"
                    stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"
                  />
                </svg>
                Attach
                <input type="file" multiple style={{ display: "none" }}
                  onChange={(e) => setFiles(e.target.files)} />
              </label>
              {files.length > 0 && (
                <span style={S.fileChip}>
                  {files[0].name}{files.length > 1 ? ` +${files.length - 1}` : ""}
                </span>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "12px", color: "#aaa" }}>Ctrl+Enter to send</span>
              <button
                style={S.sendBtn(sending || !message.trim())}
                onClick={handleReply}
                disabled={sending || !message.trim()}
                onMouseEnter={(e) => {
                  if (!sending && message.trim()) e.currentTarget.style.background = "#1558c0";
                }}
                onMouseLeave={(e) => {
                  if (!sending && message.trim()) e.currentTarget.style.background = "#1a73e8";
                }}
              >
                {sending ? "Sending…" : "Send reply ↑"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default TicketView;
